import { AbilityProvider, useAbility as useCaslAbility } from '@casl/react';
import { buildAbility, canPermission, type AppAbility, type PermissionId } from '@crm/permissions';
import { useMemo, type ReactNode } from 'react';

/**
 * Permissions in the UI, with the SAME rules as the API: `@crm/permissions` builds the CASL
 * ability from the granted ids (`<module>.<resource>.<action>`), and `@casl/react` carries it
 * through context.
 *
 * **Hiding a button is convenience, not security.** The API `PermissionGuard` is the one that
 * decides. Until login exists nothing feeds this provider, so every check answers "no".
 */
export function PermissionsProvider({
  permissions,
  children,
}: {
  permissions: readonly string[];
  children: ReactNode;
}) {
  const ability = useMemo(() => buildAbility(permissions), [permissions]);
  return <AbilityProvider value={ability}>{children}</AbilityProvider>;
}

export function useAbility(): AppAbility {
  return useCaslAbility<AppAbility>();
}

/** Imperative check, for handlers and loaders. */
export function useCan(permission: PermissionId): boolean {
  return canPermission(useAbility(), permission);
}

/**
 * Conditional rendering by permission id: `<Can permission="core.organization.update">`.
 * For subject/action checks CASL's own `<Can I="create" a="core.organization">` from
 * `@casl/react` reads the same provider.
 */
export function Can({
  permission,
  fallback = null,
  children,
}: {
  permission: PermissionId;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  return useCan(permission) ? <>{children}</> : <>{fallback}</>;
}
